import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getShoppingList,
  createShoppingListItem,
  updateShoppingListItem,
  deleteShoppingListItem
} from '../services/api';
import { API_URL } from '../config';
import ApiDebug from './ApiDebug';

const ShoppingList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState({ name: '', quantity: '' });
  const [showCompleted, setShowCompleted] = useState(true);
  const [groupBy, setGroupBy] = useState('day'); // 'day' or 'alphabetical'
  const navigate = useNavigate();

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await getShoppingList();
      setItems(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching shopping list:', error);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewItem({
      ...newItem,
      [name]: value,
    });
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItem.name.trim()) return;

    try {
      await createShoppingListItem(newItem);
      setNewItem({ name: '', quantity: '' });
      fetchItems();
    } catch (error) {
      console.error('Error adding item:', error);
    }
  };

  const handleToggleComplete = async (item) => {
    try {
      const updatedItem = { ...item, completed: !item.completed };
      await updateShoppingListItem(item.id, updatedItem);
      fetchItems();
    } catch (error) {
      console.error('Error updating item:', error);
    }
  };

  const handleDeleteItem = async (id) => {
    try {
      await deleteShoppingListItem(id);
      fetchItems();
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };
  
  // Function to navigate to the simplified shared list
  const handleShareList = async () => {
    try {
      // Navigate to the simple shared list view
      navigate('/shared-shopping');
    } catch (error) {
      console.error('Error navigating to shared list:', error);
      alert('Could not navigate to shared shopping list view.');
    }
  };
  
  // Function to handle copying the shopping list to the shared list
  const handleCopyToSharedList = async () => {
    // First check if we have any items
    if (filteredItems.length === 0) {
      alert('There are no items to share. Add some items first!');
      return;
    }
    
    try {
      // Format items for the shared list
      const formattedItems = filteredItems.map(item => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity || '',
        completed: item.completed,
        day: item.day || 'Ungrouped',
        meal_type: item.meal_type || 'Other'
      }));
      
      // Send to the shared list API
      const response = await fetch(`${API_URL}/simple-shared-list/save_list/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items_json: JSON.stringify(formattedItems),
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        alert('Shopping list copied to the shared list successfully!');
      } else {
        throw new Error(data.error || 'Failed to copy to shared list');
      }
    } catch (error) {
      console.error('Error copying to shared list:', error);
      alert('Could not copy to shared list. Please try again later.');
    }
  };

  const filteredItems = showCompleted
    ? items
    : items.filter((item) => !item.completed);
    
  // Group items based on the selected grouping method
  const groupedItems = Array.isArray(filteredItems) ? filteredItems.reduce((acc, item) => {
    if (!item || typeof item !== 'object') {
      return acc; // Skip invalid items
    }
    
    if (groupBy === 'alphabetical') {
      // Group by first letter
      const firstChar = (item.name && typeof item.name === 'string') 
        ? item.name.charAt(0).toUpperCase() 
        : '#';
      
      if (!acc[firstChar]) {
        acc[firstChar] = [];
      }
      
      acc[firstChar].push(item);
    } else {
      // Group by day and meal type
      const day = (item.day && typeof item.day === 'string') ? item.day : 'Ungrouped';
      
      if (!acc[day]) {
        acc[day] = {};
      }
      
      const mealType = (item.meal_type && typeof item.meal_type === 'string') ? item.meal_type : 'Other';
      
      if (!acc[day][mealType]) {
        acc[day][mealType] = [];
      }
      
      acc[day][mealType].push(item);
    }
    
    return acc;
  }, {}) : {};

  return (
    <div className="shopping-list">
      <h2>Shopping List</h2>
      <p>Add items manually or add them automatically from your meal plans.</p>

      <div className="row mb-4">
        <div className="col-md-8">
          <form onSubmit={handleAddItem} className="mb-3">
            <div className="row g-3">
              <div className="col-sm-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Item name"
                  name="name"
                  value={newItem.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="col-sm-4">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Quantity (e.g., 2 lbs, 3 pcs)"
                  name="quantity"
                  value={newItem.quantity}
                  onChange={handleInputChange}
                />
              </div>
              <div className="col-sm-2">
                <button type="submit" className="btn btn-primary w-100">
                  Add
                </button>
              </div>
            </div>
          </form>
        </div>
        
        <div className="col-md-4">
          <div className="d-grid gap-2">
            <button 
              className="btn btn-primary" 
              onClick={handleShareList}
              disabled={loading}
            >
              Go to Shared Shopping List
            </button>
            
            <button 
              className="btn btn-outline-primary" 
              onClick={handleCopyToSharedList}
              disabled={loading}
            >
              Copy to Shared List
            </button>
          </div>
        </div>
      </div>
      
      <div className="mb-3">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="showCompleted"
            checked={showCompleted}
            onChange={() => setShowCompleted(!showCompleted)}
          />
          <label className="form-check-label" htmlFor="showCompleted">
            Show completed items
          </label>
        </div>
        
        <div className="btn-group mt-2">
          <button
            className={`btn btn-sm ${groupBy === 'day' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setGroupBy('day')}
          >
            Group by Day/Meal
          </button>
          <button
            className={`btn btn-sm ${groupBy === 'alphabetical' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setGroupBy('alphabetical')}
          >
            Group Alphabetically
          </button>
        </div>
      </div>

      {/* Display when no items in the list */}
      {filteredItems.length === 0 && (
        <div className="alert alert-info">
          No items in your shopping list yet. Add some items or use the "Add to Shopping List" button in the Meal Planner.
        </div>
      )}

      {/* Alphabetical grouping view */}
      {groupBy === 'alphabetical' && filteredItems.length > 0 && (
        <div className="alphabetical-view">
          {Object.keys(groupedItems)
            .sort()
            .map((letter) => (
              <div key={letter} className="letter-group mb-4">
                <h3 className="letter-header">{letter}</h3>
                <ul className="list-group">
                  {groupedItems[letter].map((item) => (
                    <li key={item.id} className="list-group-item d-flex justify-content-between align-items-center">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={item.completed}
                          onChange={() => handleToggleComplete(item)}
                          id={`item-${item.id}`}
                        />
                        <label
                          className={`form-check-label ${
                            item.completed ? 'text-decoration-line-through' : ''
                          }`}
                          htmlFor={`item-${item.id}`}
                        >
                          <div>
                            {item.name}
                            {item.quantity && <span className="ms-2 text-muted">({item.quantity})</span>}
                          </div>
                        </label>
                      </div>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDeleteItem(item.id)}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </div>
      )}

      {/* Day and meal type grouping view */}
      {groupBy === 'day' && filteredItems.length > 0 && (
        <div className="day-view">
          {Object.keys(groupedItems)
            .sort((a, b) => {
              const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday', 'Ungrouped'];
              return days.indexOf(a) - days.indexOf(b);
            })
            .map((day) => (
              <div key={day} className="day-group mb-4">
                <h3 className="day-header">{day}</h3>
                {Object.keys(groupedItems[day])
                  .sort((a, b) => {
                    const mealTypes = ['Breakfast', 'Lunch', 'Dinner', 'Other'];
                    return mealTypes.indexOf(a) - mealTypes.indexOf(b);
                  })
                  .map((mealType) => (
                    <div key={`${day}-${mealType}`} className="meal-type-group mb-3">
                      <h5 className="meal-type-header">{mealType}</h5>
                      <ul className="list-group">
                        {groupedItems[day][mealType].map((item) => (
                          <li key={item.id} className="list-group-item d-flex justify-content-between align-items-center">
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={item.completed}
                                onChange={() => handleToggleComplete(item)}
                                id={`item-${item.id}`}
                              />
                              <label
                                className={`form-check-label ${
                                  item.completed ? 'text-decoration-line-through' : ''
                                }`}
                                htmlFor={`item-${item.id}`}
                              >
                                <div>
                                  {item.name}
                                  {item.quantity && <span className="ms-2 text-muted">({item.quantity})</span>}
                                </div>
                              </label>
                            </div>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteItem(item.id)}
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
              </div>
            ))}
        </div>
      )}

      <ApiDebug />
    </div>
  );
};

export default ShoppingList;